import { useState, useRef, useEffect } from 'react';
import { AppPage, AppCard } from '../../components/general';
import { Toast } from 'primereact/toast';
import { DataTableSearchCreateHeader } from '../../components/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { BlockUI } from 'primereact/blockui';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import WebhookSubscriptionService from '../../services/webhookSubscriptionService';
import { EditWebhook } from './EditWebHook';
import { WebhookSubscription } from '../../models/webhookSubscriptions';

const MaskedValue = ({ value, label }: { value?: string; label: string }) => {
  const [visible, setVisible] = useState(false);
  const displayValue = value || '—';

  return (
    <p>
      <strong>{label}:</strong>{' '}
      <span style={{ fontFamily: !visible && value ? 'monospace' : undefined }}>
        {!value ? '—' : visible ? displayValue : '••••••••••••'}
      </span>
      {value && (
        <Button
          type="button"
          icon={visible ? 'pi pi-eye-slash' : 'pi pi-eye'}
          className="p-button-text p-button-rounded p-button-sm ml-2"
          onClick={() => setVisible(!visible)}
          style={{ verticalAlign: 'middle' }}
        />
      )}
    </p>
  );
};

export const WebhookSubscriptions = (): JSX.Element => {
  const toast = useRef<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [webhook, setWebhook] = useState<WebhookSubscription | null>();
  const [_, setSearch] = useState<string>('');
  const [editorSidebarVisible, setEditorSidebarVisible] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth < 768);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookSubscription | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<WebhookSubscription | null>(null);
  const [confirmationOperation, setConfirmationOperation] = useState<'save' | 'delete'>('save');

  const webhookService = WebhookSubscriptionService.getInstance();

  useEffect(() => {
    fetchWebhook();
  }, []);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchWebhook = (): void => {
    setIsLoading(true);
    webhookService
      .getAll()
      .then((webhooks) => {
        setWebhook(webhooks);
        setIsLoading(false);
      })
      .catch(() => {
        setWebhook(null);
      })
      .finally(() => setIsLoading(false));
  };

  const showMessage = (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string,
    life: number = 3000
  ) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life,
    });
  };

  const showError = (message: string) => {
    showMessage('error', 'Error', message, 5000);
  };

  const onEdit = (webhook: WebhookSubscription): void => {
    setSelectedWebhook(webhook);
    setEditorSidebarVisible(true);
  };

  const onDelete = (webhook: WebhookSubscription) => {
    setSelectedWebhook(webhook);
    setConfirmationOperation('delete');
    setConfirmationData(webhook);
    setConfirmDialogVisible(true);
  };

  const handleConfirmAccept = () => {
    setConfirmDialogVisible(false);

    if (confirmationOperation === 'delete' && confirmationData) {
      deleteWebhook();
    } else if (confirmationOperation === 'save' && confirmationData) {
      createSubscription(confirmationData);
    }
  };

  const handleConfirmReject = () => {
    setConfirmDialogVisible(false);
  };

  const deleteWebhook = () => {
    setIsDeleting(true);
    webhookService
      .delete()
      .then(() => {
        showMessage('success', 'Success', `Webhook successfully deleted`);
        fetchWebhook();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsDeleting(false));
  };

  const createSubscription = (subscription: WebhookSubscription) => {
    setIsSubmitting(true);
    webhookService
      .create(subscription)
      .then(() => {
        showMessage('success', 'Success', `Subscription successfully created`);
        setEditorSidebarVisible(false);
        fetchWebhook();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const renderContent = () => {
    return (
      <>
        <Toast ref={toast} />
        <ConfirmDialog />

        {/* Confirmation Dialog */}
        <Dialog
          header={confirmationOperation === 'save' ? 'Confirm Changes' : 'Confirm Delete'}
          visible={confirmDialogVisible}
          onHide={handleConfirmReject}
          style={{ width: '450px' }}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button label="No" icon="pi pi-times" onClick={handleConfirmReject} className="p-button-text" />
              <Button label="Yes" icon="pi pi-check" onClick={handleConfirmAccept} autoFocus />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--yellow-500)' }} />
            {confirmationOperation === 'save' ? (
              <span>Are you sure you want to save these changes?</span>
            ) : (
              <span>Are you sure you want to delete this webhook?</span>
            )}
          </div>
        </Dialog>

        {/* Sidebar for Create/Edit */}
        <Sidebar
          visible={editorSidebarVisible}
          position="right"
          onHide={() => setEditorSidebarVisible(false)}
          className="p-sidebar-md md:p-sidebar-lg"
          style={isSmallScreen ? undefined : { width: '100%', maxWidth: '700px' }}
          header={selectedWebhook ? `Edit Webhook Subscription` : `Add Webhook Subscription`}
          fullScreen={isSmallScreen}
          modal
        >
          <BlockUI blocked={isSubmitting} fullScreen={false} className="relative">
            {isSubmitting && (
              <div
                className="flex align-items-center justify-content-center"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                }}
              >
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
              </div>
            )}
            <EditWebhook
              subscription={selectedWebhook}
              onSave={(webhook) => {
                setConfirmationData(webhook);
                setConfirmationOperation('save');
                setConfirmDialogVisible(true);
              }}
              onCancel={() => setEditorSidebarVisible(false)}
            />
          </BlockUI>
        </Sidebar>

        {isDeleting && (
          <div className="w-full mb-3">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
            <div className="text-center mt-2 text-sm text-primary">Deleting Webhook Subscription...</div>
          </div>
        )}

        {!isLoading && (
          <DataTableSearchCreateHeader
            onCreate={() => {
              setSelectedWebhook(null);
              setEditorSidebarVisible(true);
            }}
            onSearchChange={setSearch}
            searchDisabled={true}
            createNewDisabled={!!(webhook && Object.keys(webhook).length > 0)}
          />
        )}
        <div className="mt-4">
          {/* Show Card if webhook exists, otherwise show message */}
          {isLoading ? (
            <div className="flex justify-content-center p-4">
              <ProgressSpinner />
            </div>
          ) : webhook && Object.keys(webhook).length > 0 ? (
            <div className="p-4 border rounded-lg shadow-sm bg-white">
              {/* Grid layout - 2 columns on desktop, 1 on mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* LEFT: Webhook Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Webhook URL</h3>
                  <p className="mb-4 text-gray-700">{webhook.url}</p>

                  <p className="font-semibold">Registered Events:</p>
                  <p className="mb-4 text-gray-700">{webhook.events?.length ? webhook.events.join(', ') : 'None'}</p>

                  <p className="font-semibold">Additional Headers:</p>
                  {webhook.additionalHeaders?.length ? (
                    <div className="mt-2 space-y-1">
                      {webhook.additionalHeaders.map((header: { name: string; value: string }, idx: number) => (
                        <p key={idx} className="text-gray-700">
                          <span className="font-medium">{header.name}:</span> {header.value}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">None</p>
                  )}
                </div>

                {/* RIGHT: Authentication Settings */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Authorization Type</h3>
                  <p className="mb-4 text-gray-700">{webhook.authenticationSettings?.authenticationType || 'None'}</p>

                  {/* OAuth Section */}
                  {webhook.authenticationSettings?.authenticationType === 'OAuth' && (
                    <div className="space-y-1">
                      <p>
                        <strong>Token URL:</strong> {webhook.authenticationSettings?.tokenUrl}
                      </p>
                      <p>
                        <strong>Grant Type:</strong> {webhook.authenticationSettings?.grantType}
                      </p>
                      <p>
                        <strong>Client ID:</strong> {webhook.authenticationSettings?.clientId}
                      </p>
                      <MaskedValue label="Client Secret" value={webhook.authenticationSettings?.clientSecret} />
                      <p>
                        <strong>Username:</strong> {webhook.authenticationSettings?.username}
                      </p>
                      <MaskedValue label="Password" value={webhook.authenticationSettings?.password} />
                      <p>
                        <strong>Scope:</strong> {webhook.authenticationSettings?.scope}
                      </p>

                      <div className="mt-2">
                        <strong>Additional Headers:</strong>
                        {webhook.authenticationSettings?.additionalHeaders?.length ? (
                          <div className="mt-1 space-y-1">
                            {webhook.authenticationSettings.additionalHeaders.map(
                              (header: { name: string; value: string }, idx: number) => (
                                <p key={idx} className="text-gray-700">
                                  {header.name}: {header.value}
                                </p>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500">None</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ApiKey Section */}
                  {webhook.authenticationSettings?.authenticationType === 'ApiKey' && (
                    <div className="space-y-1">
                      <p>
                        <strong>API Key Name:</strong> {webhook.authenticationSettings?.apiKeyHeaderName || '—'}
                      </p>
                      <MaskedValue label="API Key Value" value={webhook.authenticationSettings?.apiKeyHeaderValue} />
                    </div>
                  )}
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex gap-2 mt-6">
                <Button
                  type="button"
                  icon="pi pi-pencil"
                  label="Edit"
                  className="p-button-success p-button-outlined"
                  onClick={() => onEdit(webhook)}
                />
                <Button
                  type="button"
                  icon="pi pi-trash"
                  label="Delete"
                  className="p-button-danger p-button-outlined"
                  onClick={() => onDelete(webhook)}
                />
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 mt-4">No webhook subscription found</div>
          )}
        </div>
      </>
    );
  };

  return (
    <AppPage title="Webhook Subscriptions" breadcrumbs="Admin - Webhook Subscriptions">
      <AppCard>{renderContent()}</AppCard>
    </AppPage>
  );
};
